import React from 'react';
import { Facebook, Twitter, Linkedin, Mail, Link2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable share row for blog posts, path pages, founder page, etc.
 * Props:
 *   url     — absolute URL to share (defaults to current page)
 *   title   — headline used in tweet / pin / email subject
 *   text    — longer description used in email body / FB quote
 *   image   — absolute URL of an image for Pinterest
 *   variant — 'gold' (Village) | 'cyan' (Gangsta AI). Default 'gold'.
 */
export default function ShareButtons({
  url,
  title = 'The Alchemy Village',
  text = 'A place for women ready to stop sleep-walking through life and start becoming who they were meant to be.',
  image = '',
  variant = 'gold',
}) {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : 'https://thealchemyvillage.com');
  const enc = encodeURIComponent;

  const targets = [
    {
      key: 'facebook',
      label: 'Facebook',
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(title)}`,
    },
    {
      key: 'x',
      label: 'X',
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(shareUrl)}`,
    },
    {
      key: 'pinterest',
      label: 'Pinterest',
      Icon: PinterestIcon,
      href: `https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&media=${enc(image)}&description=${enc(title)}`,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
    },
    {
      key: 'email',
      label: 'Email',
      Icon: Mail,
      href: `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${shareUrl}`)}`,
    },
    {
      key: 'sms',
      label: 'Text',
      Icon: MessageCircle,
      href: `sms:?&body=${enc(`${title} — ${shareUrl}`)}`,
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied! Paste it anywhere.');
    } catch {
      toast.error('Copy failed. Long-press the URL to copy.');
    }
  };

  const tryNative = async (e) => {
    if (!navigator.share) return;
    e.preventDefault();
    try {
      await navigator.share({ title, text, url: shareUrl });
    } catch {
      /* user dismissed */
    }
  };

  return (
    <div className={`share-row share-row--${variant}`} data-testid="share-buttons">
      <p className="share-row-label">Share this story</p>
      <div className="share-row-btns">
        {targets.map(({ key, label, Icon, href }) => (
          <a
            key={key}
            href={href}
            target={key === 'email' || key === 'sms' ? '_self' : '_blank'}
            rel="noopener noreferrer"
            className="share-btn"
            data-testid={`share-${key}`}
            aria-label={`Share on ${label}`}
            onClick={key === 'email' || key === 'sms' ? undefined : tryNative}
          >
            <Icon size={16} strokeWidth={1.8} />
            <span>{label}</span>
          </a>
        ))}
        <button
          type="button"
          className="share-btn"
          data-testid="share-copy"
          onClick={handleCopy}
          aria-label="Copy link"
        >
          <Link2 size={16} strokeWidth={1.8} />
          <span>Copy</span>
        </button>
      </div>
    </div>
  );
}

function PinterestIcon({ size = 16, strokeWidth = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 19l3-7" />
      <path d="M12 7a3 3 0 0 1 3 3c0 2-2 4-4 4" />
    </svg>
  );
}
